/* stylelint-disable custom-property-pattern */
<!-- eslint-disable no-mixed-operators -->
<!-- eslint-disable @typescript-eslint/no-magic-numbers -->
<script setup lang="ts">
    import FloatingActionButton from '@/components/FloatingActionButton.ce.vue';
    import IndexView from '@/views/index.ce.vue';
    import { FormKitRoot } from '@formkit/vue';
    import { type EventBusType } from './plugins/event-bus-plugin';
    import { GOTO_SECTION, WIDGET_CLOSE, WIDGET_NAVIGATE, WIDGET_OPEN, WIDGET_TOGGLE } from './constants/events';
    import { type TargetSection, type ConstellApp, type WidgetProps } from './typings/widget';

    declare global {
        interface Window {
            CONSTELL: ConstellApp
        }
    }

    defineProps<WidgetProps>();

    const { resetPathStore } = useWidgetPathStore();
    const { isMenuOpen, isQuoteOpen } = storeToRefs(useWidgetMenuStore());
    const { openWindow } = storeToRefs(useWidgetVenueStore());
    const eventBus = inject<EventBusType>('eventBus');

    const closeWidget = (): void => {
        resetPathStore();
        openWindow.value = false;
        isMenuOpen.value = false;
        isQuoteOpen.value = false;
    };

    const openWidget = (): void => {
        openWindow.value = true;
    };

    const toggleWidget = (): void => {
        openWindow.value = !openWindow.value;

        if (openWindow.value) {
            resetPathStore();
            isMenuOpen.value = false;
            isQuoteOpen.value = false;
        }
    };

    const openWidgetTarget = async(target: TargetSection, payload?: Record<string, unknown>): Promise<void> => {
        if (!openWindow.value){
            openWindow.value = true;
            await nextTick();
        }
        eventBus?.emit(GOTO_SECTION, {
            target,
            payload,
        });
    };

    const navigateWidget = (params?: { target: TargetSection, payload?: Record<string, unknown> }): void => {
        if (!params?.target) return;

        const { target, payload } = params;

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        openWidgetTarget(target, payload);
    };

    // Register widget event handlers
    const registerGlobalEvents = (): void => {
        eventBus?.on(WIDGET_CLOSE, closeWidget);
        eventBus?.on(WIDGET_OPEN, openWidget);
        eventBus?.on(WIDGET_TOGGLE, toggleWidget);
        eventBus?.on(WIDGET_NAVIGATE, navigateWidget);
    };

    // Register global CONSTELL object
    const registerGlobalObject = (): void => {
        window.CONSTELL = {
            close: () => eventBus?.emit(WIDGET_CLOSE),
            open: () => eventBus?.emit(WIDGET_OPEN),
            toggle: () => eventBus?.emit(WIDGET_TOGGLE),
            navigate: (target: TargetSection, payload?: Record<string, unknown>) => {
                eventBus?.emit(WIDGET_NAVIGATE, {
                    target,
                    payload,
                });
            },
        };
    };

    onMounted(() => {
        // Register Globals
        registerGlobalEvents();
        registerGlobalObject();
    });
</script>

<template>
    <div
        v-if="venue"
        :class="[ [position]]"
        :style="{
            '--widget-offset-x': `${offsetX}px`,
            '--widget-offset-y': `${offsetY}px`,
            ...(brandColor && { '--widget-brand-color': brandColor }),
        }"
        class="widget-root"
    >
        <FormKitRoot>
            <div
                v-if="openWindow"
                class="overlay"
            />
            <div class="floating-button">
                <FloatingActionButton
                    :open-state-label="buttonLabelClose"
                    :closed-state-label="buttonLabel"
                    :brand-color="brandColor"
                    :is-window-open="openWindow"
                    @let-the-window-grow="toggleWidget"
                />
            </div>
            <div
                id="floating-window"
                :class="[{ open: openWindow, close: !openWindow, menu: isMenuOpen, quote: isQuoteOpen } ]"
                class="floating-window"
            >
                <Transition name="fade">
                    <IndexView
                        v-show="openWindow"
                        :is-open="openWindow"
                        :venue="venue"
                        :locale="locale"
                        :initialized="openWindow"
                        :max-guest-amount="maxGuestAmount"
                        :min-guest-amount="minGuestAmount"
                        :guest-amount-step="guestAmountStep"
                        :max-budget-amount="maxBudgetAmount"
                        :min-budget-amount="minBudgetAmount"
                        :budget-step="budgetStep"
                        :brand-color="brandColor"
                    />
                </Transition>
            </div>
        </FormKitRoot>
    </div>
</template>

<style scoped lang="scss">
.overlay {
    position: fixed;
    z-index: var(--zindex-all-top);
    background: var(--color-black-space-opacity-64);
    block-size: 100%;
    inline-size: 100%;
    inset-block-start: 0;
    inset-inline-start: 0;
}

.floating-button {
    position: fixed;
    z-index: calc(var(--zindex-all-top) + 1);
}

.floating-window {
    position: fixed;
    z-index: calc(var(--zindex-all-top) + 1);
    overflow: hidden;
    border-radius: var(--border-radius-md);
    block-size: 0;
    box-shadow: 0 4px 124px 0 rgb(0 0 0 / 0.12);
    inline-size: 0;
    max-inline-size: var(--widget-max-width-mobile);
    opacity: 0;
    transform: scale(1);
    transition: opacity 1s cubic-bezier(0.2, 0.8, 0.2, 1), inline-size 250ms cubic-bezier(0.2, 0.8, 0.2, 1), max-inline-size 250ms cubic-bezier(0.2, 0.8, 0.2, 1);

    &.open {
        block-size: calc(var(--widget-height) - var(--widget-offset-y));
        inline-size: 100%;
        opacity: 1;

        @include min-width(md) {
            max-inline-size: var(--widget-max-width-quote-mobile);
        }

        @include min-width(lg) {
            max-inline-size: var(--widget-max-width-quote);
        }

        &.menu {
            @include min-width(md) {
                max-inline-size: var(--widget-max-width-menu-mobile);
            }

            @include min-width(lg) {
                max-inline-size: var(--widget-max-width-menu);
            }
        }
    }

    @include min-width(lg) {
        max-inline-size: var(--widget-max-width);
    }
}

// Positions

// eslint-disable-next-line vue-scoped-css/no-unused-selector, vue-scoped-css/require-selector-used-inside
.bottom-right {
    .floating-button {
        inset-block-end: calc(var(--widget-button-base-offset-y) + var(--widget-offset-y));
        inset-inline-end: calc(var(--widget-button-base-offset-x) + var(--widget-offset-x));
    }

    .floating-window {
        inset-block-end: calc(var(--widget-button-base-offset-y) + var(--widget-base-offset-y) + var(--size-11) + var(--widget-offset-y));
        inset-inline-end: var(--widget-button-base-offset-x);

        @include min-width(lg) {
            inset-block-end: calc(var(--widget-button-base-offset-y) + var(--widget-base-offset-y) + var(--size-11) + var(--widget-offset-y));
            inset-inline-end: calc(var(--widget-base-offset-x) + var(--widget-offset-x));
        }
    }
}

// eslint-disable-next-line vue-scoped-css/no-unused-selector, vue-scoped-css/require-selector-used-inside
.bottom-center {
    .floating-button {
        inset-block-end: calc(var(--widget-button-base-offset-y) + var(--widget-offset-y));
        inset-inline-start: 50%;
        transform: translateX(-50%);
    }

    .floating-window {
        inset-block-end: calc(var(--widget-button-base-offset-y) + var(--widget-base-offset-y) + var(--size-11) + var(--widget-offset-y));
        inset-inline-start: 50%;
        transform: translateX(-50%);

        @include min-width(md) {
            inset-block-end: calc(var(--widget-button-base-offset-y) + var(--widget-base-offset-y) + var(--size-11) + var(--widget-offset-y));
        }
    }
}

// eslint-disable-next-line vue-scoped-css/no-unused-selector, vue-scoped-css/require-selector-used-inside
.bottom-left {
    .floating-button {
        inset-block-end: calc(var(--widget-button-base-offset-y) + var(--widget-offset-y));
        inset-inline-start: calc(var(--widget-button-base-offset-x) + var(--widget-offset-x));
    }

    .floating-window {
        inset-block-end: calc(var(--widget-button-base-offset-y) + var(--widget-base-offset-y) + var(--size-11) + var(--widget-offset-y));
        inset-inline-start: var(--widget-button-base-offset-x);

        @include min-width(lg) {
            inset-block-end: calc(var(--widget-button-base-offset-y) + var(--widget-base-offset-y) + var(--size-11) + var(--widget-offset-y));
            inset-inline-start: calc(var(--widget-base-offset-x) + var(--widget-offset-x));
        }
    }
}

// eslint-disable-next-line vue-scoped-css/no-unused-selector, vue-scoped-css/require-selector-used-inside
.top-right {
    .floating-button {
        inset-block-start: calc(var(--widget-button-base-offset-y) + var(--widget-offset-y));
        inset-inline-end: calc(var(--widget-button-base-offset-x) + var(--widget-offset-x));
    }

    .floating-window {
        inset-block-start: calc(var(--widget-base-offset-y) + var(--widget-offset-y) + var(--widget-header-height-mobile));
        inset-inline-end: var(--widget-button-base-offset-x);

        @include min-width(md) {
            inset-block-start: calc(var(--widget-base-offset-y) + var(--widget-offset-y) + var(--widget-header-height-tablet));
        }

        @include min-width(lg) {
            inset-block-start: calc(var(--widget-base-offset-y) + var(--widget-offset-y) + var(--widget-header-height));
            inset-inline-end: calc(var(--widget-base-offset-x) + var(--widget-offset-x));
        }
    }
}

// eslint-disable-next-line vue-scoped-css/no-unused-selector, vue-scoped-css/require-selector-used-inside
.top-center {
    .floating-button {
        inset-block-start: calc(var(--widget-button-base-offset-y) + var(--widget-offset-y));
        inset-inline-start: 50%;
        transform: translateX(-50%);
    }

    .floating-window {
        inset-block-start: calc(var(--widget-base-offset-y) + var(--widget-offset-y) + var(--widget-header-height-mobile));
        inset-inline-start: 50%;
        transform: translateX(-50%);

        @include min-width(md) {
            inset-block-start: calc(var(--widget-base-offset-y) + var(--widget-offset-y) + var(--widget-header-height-tablet));
        }

        @include min-width(lg) {
            inset-block-start: calc(var(--widget-base-offset-y) + var(--widget-offset-y) + var(--widget-header-height));
        }
    }
}

// eslint-disable-next-line vue-scoped-css/no-unused-selector, vue-scoped-css/require-selector-used-inside
.top-left {
    .floating-button {
        inset-block-start: calc(var(--widget-button-base-offset-y) + var(--widget-offset-y));
        inset-inline-start: calc(var(--widget-button-base-offset-x) + var(--widget-offset-x));
    }

    .floating-window {
        inset-block-start: calc(var(--widget-base-offset-y) + var(--widget-offset-y) + var(--widget-header-height));
        inset-inline-start: var(--widget-button-base-offset-x);

        @include min-width(lg) {
            inset-inline-start: calc(var(--widget-base-offset-x) + var(--widget-offset-x));
        }
    }
}

:deep(.formkit-outer .formkit-inner button .formkit-selection-wrapper span),
:deep(.formkit-outer .formkit-inner .dropdown__list ul li .option span),
:deep(.formkit-outer .formkit-inner input),
:deep(.formkit-outer .formkit-inner input::placeholder),
:deep(.formkit-outer .formkit-inner textarea),
:deep(.formkit-outer .formkit-inner textarea::placeholder)  {
    /* stylelint-disable-next-line declaration-no-important */
    font-size: var(--font-medium) !important;
}
</style>
